import React from "react";
import ImageShow from "./ImageShow";

export default function DoctorPoster({
  doctorImage,
  headerText,
  logoImage,
  qrCodeImage,
  frameImage,
  doctorName = "{{name}}",
  specialization = "{{specialization}}",
  phoneNumber = "{{phone_number}}",
  showPlaceholders = true,
  frameCategory = ""
}) {
  const [loadError, setLoadError] = React.useState(false);

  const resolveUrl = (url) => {
    if (!url || url === "" || url === "undefined") return "";
    const isAbsolute = /^https?:\/\//i.test(url) || url.startsWith("data:") || url.startsWith("//");
    if (isAbsolute) return url;
    const baseurl = "https://apis.oneklicks.com";
    const normalizedPath = url.startsWith("/") ? url : `/${url}`;
    return `${baseurl}${normalizedPath}`;
  };

  const getFallbackFrame = (category) => {
    const cat = String(category || "").toLowerCase();
    if (cat === "modern" || cat === "classic") {
      return "https://img.freepik.com/free-vector/blue-medical-care-social-media-post-template-design-vector_53876-127815.jpg";
    } else if (cat === "vintage" || cat === "minimalist") {
      return "https://img.freepik.com/free-vector/clean-medical-background_53876-116875.jpg";
    } else {
      return "https://img.freepik.com/free-vector/blue-abstract-gradient-wave-vector-background_53876-111585.jpg";
    }
  };

  const resolvedFrameImage = (loadError || !frameImage) ? getFallbackFrame(frameCategory) : resolveUrl(frameImage);
  const resolvedDoctorImage = resolveUrl(doctorImage) || "https://apis.oneklicks.com/uploads/3/doctors/1746963970092_default-placeholder-doctor-halflength-portrait-600nw-1058724875.png";
  const resolvedLogoImage = resolveUrl(logoImage);
  const resolvedQrCodeImage = resolveUrl(qrCodeImage);

  return (
    <div style={{ position: "relative", width: "100%", height: "auto" }}>
      {/* Base Template */}
      <ImageShow 
        url={resolvedFrameImage} 
        alt="Poster Template" 
        style={{ 
          width: "100%", 
          height: "auto",
          display: "block"
        }} 
        onError={() => setLoadError(true)}
      />

      {/* Logo - Auto-fit positioning */}
      <div
        style={{
          position: "absolute",
          top: "3%",
          left: "3%",
          width: "auto",
          maxWidth: "15%",
          minWidth: "60px"
        }}
      >
        {showPlaceholders ? (
          <div style={{
            background: "rgba(128, 128, 128, 0.3)",
            border: "2px dashed #999",
            borderRadius: "8px",
            padding: "10px",
            textAlign: "center",
            color: "#666",
            fontSize: "14px",
            fontWeight: "bold"
          }}>
            {resolvedLogoImage ? (
              <img 
                src={resolvedLogoImage} 
                alt="Logo" 
                style={{ 
                  width: "100%", 
                  height: "auto",
                  maxHeight: "50px"
                }} 
              />
            ) : (
              "{{Logo}}"
            )}
          </div>
        ) : (
          resolvedLogoImage && (
            <img 
              src={resolvedLogoImage} 
              alt="Logo" 
              style={{ 
                width: "100%", 
                height: "auto",
                maxHeight: "50px"
              }} 
            />
          )
        )}
      </div>

      {/* Header Text - Auto-fit positioning */}
      <div
        style={{
          position: "absolute",
          top: "4%",
          left: "20%",
          right: "15%",
          fontSize: "clamp(16px, 3vw, 24px)",
          fontWeight: "bold",
          color: "#333",
          textAlign: "center"
        }}
      >
        {showPlaceholders ? (
          <div style={{
            background: "rgba(128, 128, 128, 0.3)",
            border: "2px dashed #999",
            borderRadius: "8px",
            padding: "8px 12px",
            color: "#666",
            fontSize: "14px",
            fontWeight: "bold"
          }}>
            {headerText || "{{header}}"}
          </div>
        ) : (
          headerText
        )}
      </div>

      {/* Social Media Icons - Auto-fit positioning */}
      <div
        style={{
          position: "absolute",
          top: "3%",
          right: "3%",
          display: "flex",
          gap: "8px"
        }}
      >
        {showPlaceholders && (
          <>
            <div style={{
              width: "30px",
              height: "30px",
              background: "rgba(128, 128, 128, 0.3)",
              border: "2px dashed #999",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#666",
              fontSize: "12px"
            }}>
              f
            </div>
            <div style={{
              width: "30px",
              height: "30px",
              background: "rgba(128, 128, 128, 0.3)",
              border: "2px dashed #999",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#666",
              fontSize: "12px"
            }}>
              t
            </div>
            <div style={{
              width: "30px",
              height: "30px",
              background: "rgba(128, 128, 128, 0.3)",
              border: "2px dashed #999",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#666",
              fontSize: "12px"
            }}>
              ...
            </div>
          </>
        )}
      </div>

      {/* Main Poster Area - Auto-fit positioning */}
      <div
        style={{
          position: "absolute",
          top: "15%",
          left: "5%",
          right: "5%",
          height: "50%"
        }}
      >
        {showPlaceholders && (
          <div style={{
            background: "rgba(128, 128, 128, 0.3)",
            border: "2px dashed #999",
            borderRadius: "8px",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#666",
            fontSize: "18px",
            fontWeight: "bold"
          }}>
            {"{{poster}}"}
          </div>
        )}
      </div>

      {/* Doctor Image - Auto-fit positioning */}
      <div
        style={{
          position: "absolute",
          bottom: "15%",
          left: "5%",
          width: "auto",
          maxWidth: "25%",
          minWidth: "120px"
        }}
      >
        {showPlaceholders ? (
          <div style={{
            background: "rgba(128, 128, 128, 0.3)",
            border: "2px dashed #999",
            borderRadius: "10px",
            width: "120px",
            height: "160px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#666",
            fontSize: "12px",
            fontWeight: "bold",
            textAlign: "center"
          }}>
            {resolvedDoctorImage ? (
              <img 
                src={resolvedDoctorImage} 
                alt="Doctor" 
                style={{ 
                  width: "100%", 
                  height: "100%", 
                  objectFit: "cover", 
                  borderRadius: "8px" 
                }} 
              />
            ) : (
              "{{doctor_image}}"
            )}
          </div>
        ) : (
          resolvedDoctorImage && (
            <img 
              src={resolvedDoctorImage} 
              alt="Doctor" 
              style={{ 
                width: "100%", 
                height: "auto",
                maxHeight: "160px",
                objectFit: "cover", 
                borderRadius: "10px" 
              }} 
            />
          )
        )}
      </div>

      {/* Doctor Info - Auto-fit positioning */}
      <div
        style={{
          position: "absolute",
          bottom: "20%",
          left: "32%",
          right: "25%"
        }}
      >
        {showPlaceholders ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <div style={{
              background: "rgba(128, 128, 128, 0.3)",
              border: "2px dashed #999",
              borderRadius: "6px",
              padding: "6px 10px",
              color: "#666",
              fontSize: "14px",
              fontWeight: "bold"
            }}>
              {doctorName}
            </div>
            <div style={{
              background: "rgba(128, 128, 128, 0.3)",
              border: "2px dashed #999",
              borderRadius: "6px",
              padding: "6px 10px",
              color: "#666",
              fontSize: "12px"
            }}>
              {specialization}
            </div>
            <div style={{
              background: "rgba(128, 128, 128, 0.3)",
              border: "2px dashed #999",
              borderRadius: "6px",
              padding: "6px 10px",
              color: "#666",
              fontSize: "12px"
            }}>
              {phoneNumber}
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {doctorName && (
              <div style={{ fontSize: "16px", fontWeight: "bold", color: "#333" }}>
                {doctorName}
              </div>
            )}
            {specialization && (
              <div style={{ fontSize: "14px", color: "#666" }}>
                {specialization}
              </div>
            )}
            {phoneNumber && (
              <div style={{ fontSize: "14px", color: "#666" }}>
                {phoneNumber}
              </div>
            )}
          </div>
        )}
      </div>

      {/* QR Code - Auto-fit positioning */}
      <div
        style={{
          position: "absolute",
          bottom: "15%",
          right: "5%",
          width: "auto",
          maxWidth: "15%",
          minWidth: "80px"
        }}
      >
        {showPlaceholders ? (
          <div style={{
            background: "rgba(128, 128, 128, 0.3)",
            border: "2px dashed #999",
            borderRadius: "8px",
            width: "80px",
            height: "80px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#666",
            fontSize: "10px",
            fontWeight: "bold",
            textAlign: "center"
          }}>
            {resolvedQrCodeImage ? (
              <img 
                src={resolvedQrCodeImage} 
                alt="QR Code" 
                style={{ 
                  width: "100%", 
                  height: "100%",
                  objectFit: "contain"
                }} 
              />
            ) : (
              "{{qr_code}}"
            )}
          </div>
        ) : (
          resolvedQrCodeImage && (
            <img 
              src={resolvedQrCodeImage} 
              alt="QR Code" 
              style={{ 
                width: "100%", 
                height: "auto",
                maxHeight: "80px"
              }} 
            />
          )
        )}
      </div>

      {/* Footer - Auto-fit positioning */}
      <div
        style={{
          position: "absolute",
          bottom: "5%",
          left: "5%",
          right: "5%"
        }}
      >
        {showPlaceholders && (
          <div style={{
            background: "rgba(128, 128, 128, 0.3)",
            border: "2px dashed #999",
            borderRadius: "6px",
            padding: "8px 12px",
            textAlign: "center",
            color: "#666",
            fontSize: "12px",
            fontWeight: "bold"
          }}>
            {"{{footer}}"}
          </div>
        )}
      </div>
    </div>
  );
}
